export const dumpVars = (object: any) => {
  return (
    <>
      <pre>{JSON.stringify(object, null, 2) ?? '⚠️ JSON.stringify returned undefined'}</pre>
    </>
  );
};

export const isNullOrUndefined = <T,>(value: T | null | undefined): value is null | undefined => value === null || value === undefined;
export const isNotNullOrUndefined = <T,>(value: T | null | undefined): value is T => value !== null && value !== undefined;
export const isNotBlank = (value: any) => value !== undefined && value !== null && `${value}`.length > 0;
export const isBlank = (value: any) => !isNotBlank(value);
export const isValidString = (str: string | null | undefined): boolean => !!str && str.trim().length > 0;

export const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
  arr.reduce(
    (groups, item) => {
      (groups[key(item)] ||= []).push(item);
      return groups;
    },
    {} as Record<K, T[]>,
  );

/**
 * Zpracuje ECID a ponechá část před N-tým mínusem.
 *
 * @param ecid - ECID kód (např. "123-234-234-234-3-234")
 * @param n - Počet mínusů, které mají být zachovány
 * @returns Zpracovaný ECID kód
 *
 * @example
 * processEcid("123-234-234-234-3-234", 3) // returns "123-234-234-234"
 */
export function processEcid(ecid: string, n: number): string {
  const parts = ecid?.split('-');
  if (parts == null) return ecid;
  if (parts.length <= n) {
    return ecid;
  }
  return parts.slice(0, n + 1).join('-');
}

export const arrayEqual = (array1: any[] | null | undefined, array2: any[] | null | undefined): boolean => {
  if (isNullOrUndefined(array1) && isNullOrUndefined(array2)) return true;

  if (isNullOrUndefined(array1)) return false;
  if (isNullOrUndefined(array2)) return false;

  // 1. Kontrola délky
  if (array1.length !== array2.length) {
    return false;
  }

  // 2. Porovnání prvků
  for (let i = 0; i < array1.length; i++) {
    // Používáme přísné porovnání (===) pro kontrolu, zda jsou prvky stejné.
    if (array1[i] !== array2[i]) {
      return false;
    }
  }

  // Pokud se smyčka dokončí bez nalezení rozdílů, pole jsou shodná.
  return true;
};

/**
 * Vytvoří kopii objektu a odstraní všechny klíče,
 * jejichž hodnota je striktně null nebo undefined.
 * * @param obj Vstupní objekt (typu Record<string, T>).
 * @returns Nový objekt bez klíčů s hodnotou null/undefined.
 */
export const removeNullOrUndefinedKeys = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const newObj: Partial<T> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Kontrolujeme, zda hodnota není striktně null NEBO undefined
      if (obj[key] !== null && obj[key] !== undefined) {
        newObj[key] = obj[key];
      }
    }
  }

  return newObj;
};
